import { BorrowerEntity } from './components/BorrowerEntity';
import { ConfirmEntity } from './components/ConfirmEntity';
import { StateIncorporated } from './components/StateIncorporated';
import { ShowIf } from '../../../../../components/ShowIf';
import {
    useBorrowerEntityId,
    useBorrowerEntityName,
    useBorrowerEntityStateIncorporated,
} from '../../../../../../queries/BorrowerFields/EntityFields';
import { EntityListResult } from '../../interfaces/EntityListResult';
import { useEffect } from 'react';
import { useState } from 'react';
import { Panel, PanelBorderRadius, PanelBorderStyle, PanelTheme } from '@apply/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ApplyApi } from '@apply/api';
import { applicationInsights } from '../../../../../../ApplicationInsights';
import { IEntityValidation, ValidationMessagesResult, ValidationMessageType } from '@apply/api/src/api/ApplyApi';

interface BorrowerInformationProps {
    isReview?: boolean;
    entityList: EntityListResult | undefined;
    set_disableWizardButtons?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const BorrowerInformation: React.FC<BorrowerInformationProps> = ({
    isReview = false,
    entityList,
    set_disableWizardButtons = () => {},
}) => {
    const applyApi = new ApplyApi.ApplyApi(applicationInsights);
    const [borrowerEntityId] = useBorrowerEntityId();
    const [name] = useBorrowerEntityName();
    const [stateIncoporated] = useBorrowerEntityStateIncorporated();
    const [validationMessageResult, set_validationMessageResult] = useState<ValidationMessagesResult | undefined>(undefined);

    const validateAgainstClient = (): boolean => {
        if (!entityList || (borrowerEntityId ?? 0) > 0) return true;

        const duplicate = entityList.result.some(
            (a) =>
                a.entityStateIncorporated?.toUpperCase().trim() === stateIncoporated?.toUpperCase().trim() &&
                a.entityName?.toUpperCase().trim() === name?.toUpperCase().trim()
        );

        if (duplicate) {
            set_validationMessageResult({
                hasErrors: true,
                hasWarnings: false,
                messages: [
                    {
                        message:
                            'An entity with this name and state already exists. Please select it from the "Select Borrower" drop down.',
                        messageType: ValidationMessageType.Error,
                    },
                ],
            });

            set_disableWizardButtons(true);

            return false;
        }

        set_disableWizardButtons(false);

        return true;
    };

    const validateAgainstApi = async () => {
        const entityId = borrowerEntityId ?? 0;
        const entityName = (name ?? '').trim();
        const entityStateIncorporated = (stateIncoporated ?? '').trim();

        //Validate the Entity name and state on the backend against all entities
        const data: IEntityValidation = {
            entityId: entityId,
            name: entityName,
            state: entityStateIncorporated,
        };
        const entityValidCheck = await applyApi.validateEntity(data);

        if (entityValidCheck.statusCode !== 200) throw new Error('Validation failed');

        const serverSideValidationResult = entityValidCheck.result;

        if (serverSideValidationResult && !serverSideValidationResult.hasErrors && !serverSideValidationResult.hasWarnings) {
            set_validationMessageResult(undefined);
            set_disableWizardButtons(false);

            return true;
        }
        if (serverSideValidationResult) {
            if (serverSideValidationResult.messages) {
                set_validationMessageResult(serverSideValidationResult);
            }

            //Warning Occurred - Update state variable that enables the form - user can proceed
            if (serverSideValidationResult?.hasWarnings) {
                set_disableWizardButtons(false);
            }

            //Error Occurred - Update state variable that disables the form - user cannot proceed
            if (serverSideValidationResult?.hasErrors) {
                set_disableWizardButtons(true);
            }
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (name && stateIncoporated) {
            if (validateAgainstClient()) {
                validateAgainstApi();
            }
        } else {
            set_disableWizardButtons(true);
        }
    }, [name, stateIncoporated]);

    const entityServerValidationMessages = (isError: boolean) => {
        return (
            <div style={{ display: 'flex' }}>
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ float: 'left', color: isError ? '#db0007' : 'darkOrange' }}
                />
                <div style={{ marginLeft: '10px', float: 'right', opacity: '60%' }}>
                    {' '}
                    {validationMessageResult?.messages?.map((m) => {
                        return (
                            <div>
                                <span>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: m.message,
                                        }}
                                    />
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
            <ShowIf showIf={!isReview}>
                <div
                    className='pageSubHeader'
                    style={{ marginTop: 30 }}
                >
                    Borrower Information
                </div>
            </ShowIf>
            <BorrowerEntity isReview={isReview} />
            <StateIncorporated isReview={isReview} />
            <ShowIf showIf={(validationMessageResult?.messages?.length ?? 0) > 0}>
                <ShowIf showIf={validationMessageResult?.hasWarnings}>
                    <Panel
                        theme={PanelTheme.Yellow}
                        borderRadius={PanelBorderRadius.Small}
                        borderStyle={PanelBorderStyle.None}
                    >
                        {entityServerValidationMessages(false)}
                    </Panel>
                </ShowIf>
                <ShowIf showIf={validationMessageResult?.hasErrors}>
                    <Panel
                        theme={PanelTheme.Red}
                        borderRadius={PanelBorderRadius.Small}
                        borderStyle={PanelBorderStyle.None}
                    >
                        {entityServerValidationMessages(true)}
                    </Panel>
                </ShowIf>
            </ShowIf>
            <ConfirmEntity
                required={true}
                isReview={isReview}
            />
        </>
    );
};
